/* ===========================================================================
 * Shopify Shipping Estimator — Cache-Based (No-Cart) Engine
 * ---------------------------------------------------------------------------
 * Works as both a standalone section AND as a theme block (Fabric / Horizon).
 *
 * WHY CACHE-BASED:
 *   The previous implementation built a temporary cart and called
 *   /cart/shipping_rates.json. That endpoint rates the ENTIRE cart's weight,
 *   so a visitor who already had items in their cart saw wildly inflated rates
 *   (e.g. $3,015 instead of the correct $365 for a single 94 lb product).
 *   Clearing + restoring the cart was racy and mutated the live cart.
 *
 *   This engine instead reads a pre-synced snapshot of the store's shipping
 *   zones (shop metafield `tiddy_shipping.rate_cache`, embedded in the page)
 *   and matches the CURRENT product's own weight against the weight brackets.
 *   No cart mutation, no network round-trip, deterministic results.
 *
 * Flow:
 *   1. Geolocate visitor via ipapi.co (country + region/state) — for the label
 *      and to pick the right country lookup. (Manual override still available.)
 *   2. Convert the selected variant's weight into the cache's bracket unit.
 *   3. Look up the country in the cache, filter weight brackets that cover the
 *      product weight, group by method name, keep the cheapest per method.
 *   4. Render. Re-compute instantly on variant change or manual location change.
 * ========================================================================= */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Representative postal codes per country/region (kept for the location label
  // and for parity with the old manual form; ZIP no longer affects the rate
  // because the cache only branches on country).
  // ---------------------------------------------------------------------------
  const REGION_ZIP_MAP = {
    US: {
      _default: '10001',
      AL: '35201', AK: '99501', AZ: '85001', AR: '72201', CA: '90001',
      CO: '80201', CT: '06101', DE: '19901', FL: '33101', GA: '30301',
      HI: '96801', ID: '83701', IL: '60601', IN: '46201', IA: '50301',
      KS: '66101', KY: '40201', LA: '70112', ME: '04101', MD: '21201',
      MA: '02108', MI: '48201', MN: '55101', MS: '39201', MO: '63101',
      MT: '59101', NE: '68101', NV: '89101', NH: '03101', NJ: '07001',
      NM: '87101', NY: '10001', NC: '27601', ND: '58101', OH: '43201',
      OK: '73101', OR: '97201', PA: '19101', RI: '02901', SC: '29201',
      SD: '57101', TN: '37201', TX: '75201', UT: '84101', VT: '05601',
      VA: '23218', WA: '98101', WV: '25301', WI: '53201', WY: '82001',
      DC: '20001'
    },
    CA: {
      _default: 'M5V 2T6',
      AB: 'T2P 1J9', BC: 'V6B 1A1', MB: 'R3C 0V8', NB: 'E2L 4L5',
      NL: 'A1C 5H5', NS: 'B3J 3N8', NT: 'X1A 2L9', NU: 'X0A 0H0',
      ON: 'M5V 2T6', PE: 'C1A 7K2', QC: 'H2Y 1C6', SK: 'S4P 3Y2',
      YT: 'Y1A 2C6'
    },
    GB: { _default: 'SW1A 1AA' },
    AU: {
      _default: '2000',
      NSW: '2000', VIC: '3000', QLD: '4000', SA: '5000',
      WA: '6000', TAS: '7000', NT: '0800', ACT: '2600'
    },
    DE: { _default: '10115' }, FR: { _default: '75001' }, IT: { _default: '00118' },
    ES: { _default: '28001' }, NL: { _default: '1011' }, BE: { _default: '1000' },
    IE: { _default: 'D01' }, SE: { _default: '11122' }, NO: { _default: '0150' },
    DK: { _default: '1050' }, FI: { _default: '00100' }, CH: { _default: '8001' },
    AT: { _default: '1010' }, PT: { _default: '1000-001' }, PL: { _default: '00-001' },
    JP: { _default: '100-0001' }, KR: { _default: '04524' }, SG: { _default: '018956' },
    HK: { _default: '999077' }, NZ: { _default: '6011' }, MX: { _default: '01000' },
    BR: { _default: '01000-000' }, IN: { _default: '110001' }, AE: { _default: '00000' },
    ZA: { _default: '0001' }
  };

  const COUNTRY_LIST = [
    ['US', 'United States', '🇺🇸'], ['CA', 'Canada', '🇨🇦'], ['GB', 'United Kingdom', '🇬🇧'],
    ['AU', 'Australia', '🇦🇺'], ['NZ', 'New Zealand', '🇳🇿'], ['IE', 'Ireland', '🇮🇪'],
    ['DE', 'Germany', '🇩🇪'], ['FR', 'France', '🇫🇷'], ['IT', 'Italy', '🇮🇹'], ['ES', 'Spain', '🇪🇸'],
    ['NL', 'Netherlands', '🇳🇱'], ['BE', 'Belgium', '🇧🇪'], ['CH', 'Switzerland', '🇨🇭'],
    ['AT', 'Austria', '🇦🇹'], ['PT', 'Portugal', '🇵🇹'], ['SE', 'Sweden', '🇸🇪'],
    ['NO', 'Norway', '🇳🇴'], ['DK', 'Denmark', '🇩🇰'], ['FI', 'Finland', '🇫🇮'], ['PL', 'Poland', '🇵🇱'],
    ['JP', 'Japan', '🇯🇵'], ['KR', 'South Korea', '🇰🇷'], ['SG', 'Singapore', '🇸🇬'],
    ['HK', 'Hong Kong', '🇭🇰'], ['MX', 'Mexico', '🇲🇽'], ['BR', 'Brazil', '🇧🇷'],
    ['IN', 'India', '🇮🇳'], ['AE', 'United Arab Emirates', '🇦🇪'], ['ZA', 'South Africa', '🇿🇦']
  ];

  const PROVINCES = {
    US: [
      ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],
      ['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],
      ['DC','District of Columbia'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],
      ['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
      ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],
      ['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
      ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],
      ['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],
      ['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
      ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],
      ['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],
      ['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],
      ['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming']
    ],
    CA: [
      ['AB','Alberta'],['BC','British Columbia'],['MB','Manitoba'],
      ['NB','New Brunswick'],['NL','Newfoundland and Labrador'],
      ['NS','Nova Scotia'],['NT','Northwest Territories'],['NU','Nunavut'],
      ['ON','Ontario'],['PE','Prince Edward Island'],['QC','Quebec'],
      ['SK','Saskatchewan'],['YT','Yukon']
    ],
    AU: [
      ['NSW','New South Wales'],['VIC','Victoria'],['QLD','Queensland'],
      ['SA','South Australia'],['WA','Western Australia'],
      ['TAS','Tasmania'],['NT','Northern Territory'],['ACT','Australian Capital Territory']
    ]
  };

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------
  function formatMoney(amount, currency) {
    if (amount === null || amount === undefined) return '–';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: currency || 'USD', minimumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return '$' + parseFloat(amount).toFixed(2);
    }
  }

  function pickZip(cc, region) {
    const c = REGION_ZIP_MAP[cc];
    if (!c) return '';
    if (region && c[region]) return c[region];
    return c._default || '';
  }

  function normalizeRegionCode(cc, raw) {
    if (!raw) return '';
    return String(raw).toUpperCase().trim();
  }

  function countryFlag(code) {
    const e = COUNTRY_LIST.find(function (c) { return c[0] === code; });
    return e ? e[2] : '';
  }
  function countryName(code) {
    const e = COUNTRY_LIST.find(function (c) { return c[0] === code; });
    return e ? e[1] : code;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c];
    });
  }

  function parseRateNameMap(text) {
    const out = {};
    if (!text) return out;
    String(text).split('\n').forEach(function (line) {
      const m = line.split('=>');
      if (m.length === 2) {
        const k = m[0].trim(), v = m[1].trim();
        if (k) out[k.toLowerCase()] = v;
      }
    });
    return out;
  }

  // Normalize a rate name so the same method typed inconsistently across zones
  // (e.g. "(In rare cases up to 2)" vs "(in rare cases up to 2)") groups together.
  function rateKey(name) {
    return String(name || '')
      .toLowerCase()
      .replace(/[()]/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  // ---------------------------------------------------------------------------
  // Weight conversion: variant weight (in its own unit) -> cache bracket unit.
  // Shopify product/variant `weight` is expressed in `weight_unit`
  // (g / kg / oz / lb). Shipping-zone brackets in the cache are expressed in
  // `cache.weight_unit` (this store: lb). We normalise both via grams.
  // ---------------------------------------------------------------------------
  const TO_GRAMS = { g: 1, kg: 1000, oz: 28.349523125, lb: 453.59237 };

  function variantWeightToGrams(value, unit) {
    const u = String(unit || 'g').toLowerCase();
    const f = TO_GRAMS[u] != null ? TO_GRAMS[u] : 1;
    return (parseFloat(value) || 0) * f;
  }

  function gramsToCacheUnit(grams, cacheUnit) {
    const u = String(cacheUnit || 'kg').toLowerCase();
    const f = TO_GRAMS[u] != null ? TO_GRAMS[u] : 1000; // default kg
    return grams / f;
  }

  // ---------------------------------------------------------------------------
  // Cache lookup
  // ---------------------------------------------------------------------------
  // cache.lookup[CC] = { n, r: [ {n,p,t} | {n,p,wl,wh,t:'w'} | {n,p:null,t:'c'} ] }
  function lookupCountryRates(cache, countryCode) {
    const lookup = (cache && cache.lookup) || {};
    if (lookup[countryCode] && lookup[countryCode].r && lookup[countryCode].r.length) {
      return lookup[countryCode].r;
    }
    // Rest-of-world fallbacks
    var fallbacks = ['ZZ', 'XX', 'ROW', '*'];
    for (var i = 0; i < fallbacks.length; i++) {
      var e = lookup[fallbacks[i]];
      if (e && e.r && e.r.length) return e.r;
    }
    return [];
  }

  // Filter weight brackets to the one covering the product weight, group by
  // method name, keep the cheapest matching price per method (mirrors what
  // Shopify actually offers the customer).
  function resolveRates(compactRates, productWeightInCacheUnit) {
    const w = productWeightInCacheUnit; // may be null
    const groups = new Map();

    function consider(name, price, type) {
      const key = rateKey(name) || name;
      const existing = groups.get(key);
      if (!existing) { groups.set(key, { name: name, price: price, type: type }); return; }
      if (existing.price === null && price !== null) {
        groups.set(key, { name: name, price: price, type: type });
      } else if (price !== null && existing.price !== null && price < existing.price) {
        groups.set(key, { name: name, price: price, type: type });
      }
    }

    for (var i = 0; i < compactRates.length; i++) {
      var r = compactRates[i];
      if (r.t === 'w') {
        if (w !== null) {
          var lo = (r.wl != null) ? r.wl : 0;
          var hi = (r.wh != null) ? r.wh : Infinity;
          // Brackets are inclusive on both ends in Shopify; use [lo, hi].
          if (w < lo || w > hi) continue;
        }
        consider(r.n, r.p, 'flat');
        continue;
      }
      consider(r.n, r.p, r.t === 'c' ? 'carrier' : 'flat');
    }

    var result = Array.from(groups.values());
    result.sort(function (a, b) {
      if (a.price === null) return 1;
      if (b.price === null) return -1;
      return (a.price == null ? 1e12 : a.price) - (b.price == null ? 1e12 : b.price);
    });
    return result;
  }

  // ---------------------------------------------------------------------------
  // ipapi.co geolocation (cached in sessionStorage)
  // ---------------------------------------------------------------------------
  function geolocate() {
    const CACHE_KEY = 'se_geoloc_v1';
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (e) {}

    return fetch('https://ipapi.co/json/', { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('ipapi ' + r.status); return r.json(); })
      .then(function (data) {
        if (data && data.error) throw new Error(data.reason || 'ipapi error');
        const result = {
          country: data.country_code || data.country || '',
          region: data.region_code || data.region || '',
          regionName: data.region || '',
          city: data.city || ''
        };
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(result)); } catch (e) {}
        return result;
      });
  }

  // ---------------------------------------------------------------------------
  // Renderer
  // ---------------------------------------------------------------------------
  function renderRates(root, rates, config) {
    const container = root.querySelector('[data-rates-container]');
    if (!container) return;

    if (!rates || rates.length === 0) {
      container.innerHTML =
        '<p class="shipping-estimator__empty">' + escapeHtml(config.noRatesText) + '</p>';
      return;
    }

    const nameMap = parseRateNameMap(config.rateNameMap);
    const descMap = parseRateNameMap(config.rateDescMap);
    const freeText = config.freeText || 'FREE';

    const items = rates.map(function (rate) {
      var priceLabel;
      if (rate.type === 'carrier' || rate.price === null) {
        priceLabel = '<strong class="shipping-estimator__rate-meta">' +
          escapeHtml(config.carrierText || 'Calculated at checkout') + '</strong>';
      } else if (Number(rate.price) === 0) {
        priceLabel = '<strong class="shipping-estimator__free">' + escapeHtml(freeText) + '</strong>';
      } else {
        priceLabel = '<strong>' + formatMoney(rate.price, config.currency) + '</strong>';
      }

      const override = nameMap[String(rate.name).toLowerCase()];
      const displayName = override || rate.name;
      const desc = descMap[String(rate.name).toLowerCase()] ||
                   descMap[String(displayName).toLowerCase()];
      const descHtml = desc
        ? '<small class="shipping-estimator__rate-desc">' + escapeHtml(desc) + '</small>'
        : '';

      return (
        '<li class="shipping-estimator__rate">' +
          '<span class="shipping-estimator__rate-name">' + escapeHtml(displayName) + descHtml + '</span>' +
          '<span class="shipping-estimator__rate-price">' + priceLabel + '</span>' +
        '</li>'
      );
    }).join('');

    var extras = '';
    var priced = rates.filter(function (r) {
      return r.price !== null && r.type !== 'carrier' && Number(r.price) > 0;
    });
    if (config.savingsText && priced.length > 1) {
      var prices = priced.map(function (r) { return Number(r.price); });
      var diff = Math.max.apply(null, prices) - Math.min.apply(null, prices);
      if (diff > 0.5) {
        extras += '<p class="shipping-estimator__savings">' +
          escapeHtml(config.savingsText).replace('{amount}', formatMoney(diff, config.currency)) +
          '</p>';
      }
    }
    if (config.anchorText && Number(config.productWeightLb) > 0) {
      extras += '<p class="shipping-estimator__anchor">' +
        escapeHtml(config.anchorText).replace('{weight}', String(config.productWeightLb)) +
        '</p>';
    }
    if (config.whyText && config.whyBody) {
      extras += '<button type="button" class="shipping-estimator__why" data-why-toggle aria-expanded="false">' +
        escapeHtml(config.whyText) + '</button>' +
        '<div class="shipping-estimator__why-body" data-why-body hidden>' +
        escapeHtml(config.whyBody) + '</div>';
    }

    container.innerHTML = '<ul class="shipping-estimator__rate-list">' + items + '</ul>' + extras;

    var whyBtn = container.querySelector('[data-why-toggle]');
    if (whyBtn) {
      whyBtn.addEventListener('click', function () {
        var body = container.querySelector('[data-why-body]');
        if (!body) return;
        body.hidden = !body.hidden;
        whyBtn.setAttribute('aria-expanded', body.hidden ? 'false' : 'true');
      });
    }
  }

  function setLocationLabel(root, destination, config) {
    const label = root.querySelector('[data-location-text]');
    const spinner = root.querySelector('.shipping-estimator__subtitle .shipping-estimator__spinner');
    if (!label) return;
    if (spinner) spinner.style.display = 'none';

    const flag = config.showFlag ? (countryFlag(destination.country) + ' ') : '';
    const name = countryName(destination.country) || destination.country || '—';
    const region = destination.province ? destination.province + ', ' : '';
    label.textContent = (config.shippingToText || 'Shipping to') + ' ' +
                        flag + region + name +
                        (destination.zip ? ' ' + destination.zip : '');
  }

  function setEmpty(root) {
    const container = root.querySelector('[data-rates-container]');
    if (container) container.innerHTML = '';
  }

  function setError(root, config) {
    const container = root.querySelector('[data-rates-container]');
    if (container) {
      container.innerHTML =
        '<p class="shipping-estimator__error">' + escapeHtml(config.errorText) + '</p>';
    }
  }

  // ---------------------------------------------------------------------------
  // Manual form helpers
  // ---------------------------------------------------------------------------
  function populateCountrySelect(select, useFlags) {
    if (!select || select.options.length > 1) return;
    COUNTRY_LIST.forEach(function (pair) {
      const opt = document.createElement('option');
      opt.value = pair[0];
      opt.textContent = (useFlags ? (pair[2] + '  ') : '') + pair[1];
      select.appendChild(opt);
    });
  }

  function populateProvinceSelect(select, cc, placeholder) {
    if (!select) return;
    select.innerHTML = '<option value="">' + escapeHtml(placeholder || 'Select…') + '</option>';
    const list = PROVINCES[cc];
    if (!list) return;
    list.forEach(function (pair) {
      const opt = document.createElement('option');
      opt.value = pair[0];
      opt.textContent = pair[1];
      select.appendChild(opt);
    });
  }

  // ---------------------------------------------------------------------------
  // Variant-change listener — recompute weight & rates instantly
  // ---------------------------------------------------------------------------
  function bindVariantUpdates(root, onVariant) {
    document.addEventListener('variant:change', function (e) {
      const v = e.detail && (e.detail.variant || e.detail);
      if (v && v.id) onVariant(v.id);
    });
    document.addEventListener('change', function (e) {
      if (e.target && e.target.matches('input[name="id"], select[name="id"]')) {
        const newId = parseInt(e.target.value, 10);
        if (newId) onVariant(newId);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------
  function init(root) {
    const blockId = root.getAttribute('data-block-id');
    const sectionId = root.getAttribute('data-section-id');
    const configKey = blockId || sectionId;
    const config = (window.ShippingEstimatorConfig || {})[configKey] || {};
    config.currency       = config.currency || 'USD';
    config.shippingToText = config.shippingToText || 'Shipping to';
    config.noRatesText    = config.noRatesText || 'No shipping options available.';
    config.errorText      = config.errorText || 'Could not load shipping rates.';

    if (root.__seInitialized) return;
    root.__seInitialized = true;

    // ── Load the embedded rate cache ───────────────────────────────────────
    var cache = null;
    var cacheEl = document.getElementById('ShippingRateCache-' + configKey);
    if (cacheEl) {
      try { cache = JSON.parse(cacheEl.textContent); } catch (e) { cache = null; }
    }
    // A bare "null"/"" means the metafield was empty.
    if (cache && typeof cache !== 'object') cache = null;

    // ── Current variant + weight ───────────────────────────────────────────
    var currentVariantId = parseInt(root.getAttribute('data-variant-id'), 10) || config.variantId;
    var variantWeights = config.variantWeights || {};

    function currentWeightGrams() {
      // Shopify Liquid `variant.weight` is ALWAYS in grams, regardless of the
      // variant's *display* weight_unit. The embedded value is therefore already
      // grams and must NOT be re-converted using the display unit — doing so
      // multiplied a 42,638 g value by 453 and pushed every heavy product past
      // the top weight bracket, producing "No shipping options available".
      var rec = variantWeights[String(currentVariantId)];
      if (rec && rec.g != null && rec.g !== '') {
        var gr = parseFloat(rec.g);
        if (!isNaN(gr)) return gr;
      }
      var g = parseFloat(root.getAttribute('data-variant-weight'));
      if (!isNaN(g)) return g; // already grams
      return null;
    }

    const countrySelect = root.querySelector('[data-country-select]');
    const provinceSelect = root.querySelector('[data-province-select]');
    const provinceField = root.querySelector('[data-province-field]');
    const zipInput = root.querySelector('[data-zip-input]');
    const manualForm = root.querySelector('[data-manual-form]');
    const toggleBtn = root.querySelector('[data-toggle-manual]');

    populateCountrySelect(countrySelect, !!config.showFlag);

    let currentDestination = { country: '', province: '', zip: '' };

    function runEstimate(destination) {
      const dest = destination || currentDestination;
      if (!dest.country) return;
      currentDestination = dest;
      setLocationLabel(root, dest, config);

      if (!cache) { setError(root, config); return; }

      const cacheUnit = cache.weight_unit || 'lb';
      const grams = currentWeightGrams();
      const weightInCacheUnit = (grams === null) ? null : gramsToCacheUnit(grams, cacheUnit);

      const compact = lookupCountryRates(cache, dest.country);
      const rates = resolveRates(compact, weightInCacheUnit);
      renderRates(root, rates, config);
    }

    if (toggleBtn && manualForm) {
      toggleBtn.addEventListener('click', function () {
        const isHidden = manualForm.hasAttribute('hidden');
        if (isHidden) {
          manualForm.removeAttribute('hidden');
          toggleBtn.setAttribute('aria-expanded', 'true');
        } else {
          manualForm.setAttribute('hidden', '');
          toggleBtn.setAttribute('aria-expanded', 'false');
        }
      });
    }

    if (countrySelect) {
      countrySelect.addEventListener('change', function () {
        const cc = countrySelect.value;
        if (PROVINCES[cc] && provinceField) {
          populateProvinceSelect(provinceSelect, cc);
          provinceField.removeAttribute('hidden');
        } else if (provinceField) {
          provinceField.setAttribute('hidden', '');
          if (provinceSelect) provinceSelect.innerHTML = '';
        }
      });
    }

    if (manualForm) {
      manualForm.addEventListener('submit', function (e) {
        e.preventDefault();
        const cc = countrySelect ? countrySelect.value : '';
        if (!cc) return;
        const prov = provinceSelect ? provinceSelect.value : '';
        let zip = zipInput ? zipInput.value.trim() : '';
        if (!zip) zip = pickZip(cc, prov);
        runEstimate({ country: cc, province: prov, zip: zip });
      });
    }

    bindVariantUpdates(root, function (newId) {
      currentVariantId = newId;
      root.setAttribute('data-variant-id', newId);
      var rec = variantWeights[String(newId)];
      if (rec) {
        root.setAttribute('data-variant-weight', rec.g);
        root.setAttribute('data-variant-weight-unit', rec.u);
      }
      runEstimate();
    });

    // ── Auto-detect or wait for manual form ────────────────────────────────
    if (config.autoDetect === false || config.autoDetect === 'false') {
      const country = config.shopCountry || 'US';
      if (countrySelect) { countrySelect.value = country; countrySelect.dispatchEvent(new Event('change')); }
      if (zipInput) zipInput.value = pickZip(country, '');
      const subtitle = root.querySelector('[data-location-label]');
      if (subtitle) subtitle.style.display = 'none';
      setEmpty(root);
      return;
    }

    geolocate()
      .then(function (loc) {
        const country = loc.country || config.shopCountry || 'US';
        const province = normalizeRegionCode(country, loc.region);
        const zip = pickZip(country, province);
        if (countrySelect) { countrySelect.value = country; countrySelect.dispatchEvent(new Event('change')); }
        if (provinceSelect && province) provinceSelect.value = province;
        if (zipInput) zipInput.value = zip;
        runEstimate({ country: country, province: province, zip: zip });
      })
      .catch(function (err) {
        const country = config.shopCountry || 'US';
        const zip = pickZip(country, '');
        if (countrySelect) { countrySelect.value = country; countrySelect.dispatchEvent(new Event('change')); }
        if (zipInput) zipInput.value = zip;
        runEstimate({ country: country, province: '', zip: zip });
      });
  }

  function boot() {
    document.querySelectorAll('[data-shipping-estimator]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-render hooks
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-shipping-estimator]').forEach(function (root) {
      root.__seInitialized = false;
      init(root);
    });
  });
  document.addEventListener('shopify:block:select', function (e) {
    const root = e.target.querySelector('[data-shipping-estimator]') ||
                 (e.target.matches && e.target.matches('[data-shipping-estimator]') ? e.target : null);
    if (root && !root.__seInitialized) init(root);
  });
})();
